import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, XCircle, FileSignature } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Client portal card for the Engagement & Consent Authorization — the same
 * legal consent that appears on the last page of their worksheet PDF. Lets the
 * client type-to-sign in the portal instead of printing/scanning.
 */
export default function EngagementAuthorizationCard({ embedded = false }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/portal/engagement-authorization")
      .then(({ data }) => setState(data))
      .catch(() => toast.error("Could not load authorization status."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const sign = async () => {
    if (!signedName.trim() || signedName.trim().length < 2) {
      toast.error("Type your full name to sign."); return;
    }
    if (!agreed) { toast.error("Please check the authorization box."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/portal/engagement-authorization", {
        signed_name: signedName.trim(), agreed: true,
      });
      setState(data);
      setShowForm(false); setSignedName(""); setAgreed(false);
      toast.success("Authorization signed — a copy was emailed to you.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save authorization.");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm(
      "Revoke your engagement authorization? We'll pause access to your records until you re-sign.",
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/portal/engagement-authorization/revoke");
      setState(data);
      toast.success("Authorization revoked. We've notified your Elevare team.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke right now.");
    } finally { setBusy(false); }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
    catch { return iso; }
  };

  const isActive = state?.status === "active";
  const isRevoked = state?.status === "revoked";

  return (
    <div data-testid="portal-engagement-authorization" className={embedded ? "" : "rounded-2xl border border-[#1e3d3a]/12 bg-white p-6"}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961] flex items-center gap-1.5">
          <FileSignature className="h-3 w-3" /> Engagement authorization
        </span>
        {isActive && (
          <span data-testid="engagement-active-chip" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] bg-[#6b7c3a]/12 text-[#4d5a24] px-2.5 py-1 rounded-full">
            <ShieldCheck className="h-3 w-3" /> Signed
          </span>
        )}
        {isRevoked && (
          <span data-testid="engagement-revoked-chip" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] bg-[#b23a48]/10 text-[#b23a48] px-2.5 py-1 rounded-full">
            <XCircle className="h-3 w-3" /> Revoked
          </span>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-[#1e3d3a]/55">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading authorization…
        </div>
      )}

      {!loading && isActive && (
        <div className="space-y-3" data-testid="engagement-signed-summary">
          <p className="text-sm text-[#1e3d3a] leading-relaxed">
            You authorized Elevare Solutions to access and work with your records on{" "}
            <strong>{fmtDate(state.authorized_at)}</strong>, signed as{" "}
            <strong>{state.signed_name}</strong>.
          </p>
          <p className="text-xs text-[#1e3d3a]/65 leading-relaxed">
            This covers your banking, financial records, BBB profile, vendor contracts, payroll, and
            operational systems — the access we need to bring your business back into good standing.
            Terms version on file: <span className="font-mono text-[10px]">{state.terms_version}</span>.
          </p>
          <button type="button" data-testid="engagement-revoke" onClick={revoke} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#b23a48]/40 text-[#b23a48] px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#b23a48]/8 disabled:opacity-60 transition-colors duration-300">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
            Revoke authorization
          </button>
        </div>
      )}

      {!loading && !isActive && (
        <div className="space-y-3">
          {isRevoked && (
            <div className="text-xs text-[#1e3d3a]/60 border-l-2 border-[#b23a48]/40 pl-3">
              You revoked your authorization on {fmtDate(state.revoked_at)}. You can re-sign below anytime.
            </div>
          )}
          <p className="text-sm text-[#1e3d3a]/75 leading-relaxed">
            To let Elevare work on your behalf, we need a signed authorization granting access to your
            banking, financial records, BBB profile, vendor contracts, payroll, and operational systems.
            Sign here and you're done — no printing required.
          </p>
          {!showForm && (
            <button type="button" data-testid="engagement-open" onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
              <FileSignature className="h-3.5 w-3.5" /> Review &amp; sign authorization
            </button>
          )}
          {showForm && (
            <div className="rounded-xl border border-[#c9a961]/30 bg-[#c9a961]/5 p-4 space-y-3" data-testid="engagement-form">
              <div className="text-xs text-[#1e3d3a] leading-relaxed max-h-56 overflow-y-auto pr-2 border-l-2 border-[#c9a961]/50 pl-3">
                <p className="font-semibold mb-2">Authorization &amp; Consent to Engage</p>
                <p>
                  I authorize <strong>Elevare Solutions</strong> and its authorized personnel to review,
                  access, obtain, and work with the following for the purpose of diagnosing, correcting,
                  and improving my company's financial and operational health and restoring it to good standing:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Business bank &amp; deposit accounts, balances, statements and secure connections</li>
                  <li>Financial records &amp; bookkeeping (ledgers, P&amp;L, invoices, accounting software)</li>
                  <li>BBB profile, rating, complaints and reviews (including responding on my behalf)</li>
                  <li>Vendor &amp; supplier contracts and recurring billing</li>
                  <li>Payroll &amp; personnel expense records</li>
                  <li>Point-of-sale, scheduling and other operational systems</li>
                  <li>Tax &amp; compliance documents reasonably necessary to the engagement</li>
                </ul>
                <p className="mt-2">
                  Access is used solely for my company's benefit and will not be sold or disclosed except
                  as needed to deliver the services or as required by law. I may revoke this authorization
                  at any time from my portal. I represent that I am authorized to bind the company.
                </p>
              </div>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60 mb-1 block">
                  Type your full legal name to sign
                </span>
                <input data-testid="engagement-name" value={signedName}
                  onChange={(e) => setSignedName(e.target.value)}
                  className="w-full font-head text-lg text-[#1e3d3a] rounded border-b-2 border-[#1e3d3a]/30 bg-transparent px-1 py-1 focus:border-[#c9a961] focus:outline-none"
                  placeholder="e.g. Dana Summit" />
              </label>

              <label className="flex items-start gap-2 text-xs text-[#1e3d3a]/80 leading-relaxed">
                <input type="checkbox" data-testid="engagement-agree" checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 accent-[#1e3d3a]" />
                <span>
                  I have read and agree to this Authorization &amp; Consent to Engage, and I understand this
                  is a legally binding electronic signature under the U.S. E-SIGN Act.
                </span>
              </label>

              <div className="flex items-center gap-2">
                <button type="button" data-testid="engagement-sign" onClick={sign}
                  disabled={busy || !signedName.trim() || !agreed}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                  Sign authorization
                </button>
                <button type="button" onClick={() => { setShowForm(false); setAgreed(false); setSignedName(""); }}
                  className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] px-3 py-2 transition-colors duration-300">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
